"use client";

import Link from "@/components/ui/Link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

interface SearchHit {
  toolGroupId: number;
  name: string;
  slug: string;
  description: string | null;
  toolCount: number;
  categorySlug: string;
  categoryName: string | null;
  categoryEmoji: string | null;
  screenshotKey: string | null;
}

const DEBOUNCE_MS = 120;

export default function CommandPalette() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Open palette via ⌘K / Ctrl+K / `/`. Close via Esc.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const isTyping = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable;

      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }

      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !isTyping) {
        e.preventDefault();
        setOpen(true);
        return;
      }

      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  // Listen for programmatic open requests (e.g., header button).
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-command-palette", onOpen);
    return () => window.removeEventListener("open-command-palette", onOpen);
  }, []);

  // Focus input + reset state when opening.
  useEffect(() => {
    if (open) {
      setActiveIndex(0);
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      document.body.style.overflow = "hidden";
      return () => {
        clearTimeout(t);
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  // Debounced fetch.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(trimmed)}&limit=10`, {
          signal: ctrl.signal,
        });
        if (!res.ok) {
          setResults([]);
        } else {
          const data = (await res.json()) as { results: SearchHit[] };
          setResults(data.results ?? []);
          setActiveIndex(0);
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setResults([]);
  }, []);

  const navigate = useCallback(
    (hit: SearchHit) => {
      router.push(`/tools/${hit.categorySlug}/${hit.slug}`);
      close();
    },
    [router, close]
  );

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const hit = results[activeIndex];
      if (hit) {
        navigate(hit);
      } else if (query.trim().length >= 2) {
        router.push(`/tools?q=${encodeURIComponent(query.trim())}`);
        close();
      }
    }
  };

  // Keep the active result visible.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!open) return null;

  const showEmpty = query.trim().length >= 2 && !loading && results.length === 0;
  const showHint = query.trim().length < 2;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[10vh] sm:pt-[15vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Search tools"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close search"
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm dark:bg-black/60"
        onClick={close}
      />

      <div className="relative w-full max-w-2xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center gap-3 border-b border-gray-200 px-4 dark:border-gray-700">
          <svg
            className="h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="8.5" cy="8.5" r="5.5" />
            <path d="M12.5 12.5L17 17" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search 8,300+ tools…"
            className="w-full bg-transparent py-4 text-base text-gray-900 placeholder-gray-500 outline-none dark:text-gray-100 dark:placeholder-gray-400"
            aria-label="Search tools"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && (
            <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 dark:border-gray-600 dark:border-t-blue-400" />
          )}
          <kbd className="hidden shrink-0 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 sm:inline-flex dark:border-gray-600 dark:text-gray-400">
            Esc
          </kbd>
        </div>

        {showHint && (
          <div className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
            Start typing to search across {`8,300+ `}tools, tags, and categories.
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs">
              <span>Try</span>
              {["password generator", "json formatter", "loan calculator", "color picker"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setQuery(s)}
                  className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {showEmpty && (
          <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>No matches for &ldquo;<span className="font-medium">{query}</span>&rdquo;.</p>
            <Link
              href="/tools"
              onClick={close}
              className="mt-3 inline-block rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Browse all tools →
            </Link>
          </div>
        )}

        {results.length > 0 && (
          <ul ref={listRef} className="max-h-[60vh] overflow-y-auto py-2" role="listbox">
            {results.map((hit, idx) => {
              const isActive = idx === activeIndex;
              return (
                <li key={hit.toolGroupId} data-idx={idx} role="option" aria-selected={isActive}>
                  <button
                    type="button"
                    onClick={() => navigate(hit)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors ${
                      isActive
                        ? "bg-blue-50 dark:bg-blue-900/30"
                        : "hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                  >
                    <span className="text-xl leading-none" aria-hidden="true">
                      {hit.categoryEmoji ?? "🔧"}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-2">
                        <span className="truncate font-medium text-gray-900 dark:text-gray-100">
                          {hit.name}
                        </span>
                        {hit.toolCount > 1 && (
                          <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                            {hit.toolCount} options
                          </span>
                        )}
                      </span>
                      {hit.description && (
                        <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">
                          {hit.description}
                        </span>
                      )}
                      <span className="mt-0.5 block text-xs text-gray-400 dark:text-gray-400">
                        {hit.categoryName ?? "Tools"}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex items-center justify-between gap-4 border-t border-gray-200 bg-gray-50 px-4 py-2 text-[11px] text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-gray-300 bg-white px-1 dark:border-gray-600 dark:bg-gray-900">↑</kbd>
              <kbd className="rounded border border-gray-300 bg-white px-1 dark:border-gray-600 dark:bg-gray-900">↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-gray-300 bg-white px-1 dark:border-gray-600 dark:bg-gray-900">↵</kbd>
              open
            </span>
          </span>
          {results.length > 0 && query.trim().length >= 2 && (
            <Link
              href={`/tools?q=${encodeURIComponent(query.trim())}`}
              onClick={close}
              className="font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              See all results →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
